import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_MAP_COUNTRY, type MapCountry } from './countries';

export interface Viewport {
  center: [number, number];
  zoom: number;
}

interface ViewportState {
  /** Страна, к которой относится текущий вьюпорт. */
  cc: MapCountry;
  center: [number, number];
  zoom: number;
  /** Сохранённый вьюпорт на каждую страну — иначе Италия «переезжает» на другую карту. */
  byCountry: Partial<Record<MapCountry, Viewport>>;
  /** true, пока стартовый вид не задан ни URL, ни сохранённым значением. */
  needsMetaDefaults: boolean;
  /** Вьюпорт пришёл из URL — он приоритетнее и meta его не перебивает. */
  viewportFromUrl: boolean;

  setViewport: (center: [number, number], zoom: number) => void;
  /** Переключение страны: поднять её сохранённый вид, либо запросить дефолты из meta. */
  hydrateCountry: (cc: MapCountry) => void;
  /** Применить center/zoom из meta.json страны, если их не задали URL или persist. */
  applyMetaDefaults: (center: [number, number], zoom: number) => void;
}

// Дефолт — Италия: она остаётся страной по умолчанию, пока роут не сказал иного.
const IT_FALLBACK: Viewport = { center: [45.8, 10.5], zoom: 7 };

export const useMapStore = create<ViewportState>()(
  persist(
    (set, get) => ({
      cc: DEFAULT_MAP_COUNTRY,
      center: IT_FALLBACK.center,
      zoom: IT_FALLBACK.zoom,
      byCountry: {},
      needsMetaDefaults: true,
      viewportFromUrl: false,

      setViewport: (center, zoom) =>
        set((s) => ({
          center,
          zoom,
          byCountry: { ...s.byCountry, [s.cc]: { center, zoom } },
          needsMetaDefaults: false,
        })),

      hydrateCountry: (cc) => {
        const saved = get().byCountry[cc];
        if (saved) {
          set({ cc, center: saved.center, zoom: saved.zoom, needsMetaDefaults: false });
        } else {
          set({ cc, needsMetaDefaults: true });
        }
      },

      applyMetaDefaults: (center, zoom) => {
        const s = get();
        if (!s.needsMetaDefaults || s.viewportFromUrl) return;
        set({ center, zoom, needsMetaDefaults: false });
      },
    }),
    {
      name: 'relocation_viewport_v1',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ byCountry: state.byCountry }),
      // v1 хранил плоские center/zoom без страны — это была Италия.
      migrate: (persisted, version) => {
        if (version >= 2) return persisted as { byCountry: ViewportState['byCountry'] };
        const old = persisted as Partial<Viewport> | null;
        if (old?.center && typeof old.zoom === 'number') {
          return { byCountry: { it: { center: old.center, zoom: old.zoom } } };
        }
        return { byCountry: {} };
      },
      // После восстановления byCountry поднимаем вид текущей страны.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const saved = state.byCountry[state.cc];
        if (saved && !state.viewportFromUrl) {
          state.center = saved.center;
          state.zoom = saved.zoom;
          state.needsMetaDefaults = false;
        }
      },
    },
  ),
);
